import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Portal,
  Paper,
} from "@mui/material";
import * as turf from "@turf/turf";
import { StepperCustom } from "../../ui/stepper";
import {
  PROPERTY_SOURCE,
  UNITS_DEFAULT,
  UPLOAD_STEPS,
  UPLOADED_GEOJSON_FEATURE_ID,
} from "../../../constants/site-settings.constants";
import { StepUpload } from "./step-upload";
import { StepUnits } from "./step-units";
import { StepCenter } from "./step-center";
import { StepRotate } from "./step-rotate";
import { reverseGeocode } from "../../../utils/map-labels";
import { useAppContext } from "../../../services/context/app-context";
import { useSiteSettingsContext } from "../../../services/context/site-settings-context";
import { luckyorangeTrackEven } from "../../../services/lucky-orange/luckyorange-events";

export const SiteUploadPopup = ({
  open,
  onClose,
  updatePropertyData,
  propertyData,
  deleteDrawing,
}) => {
  const isFile = propertyData.propertySource === PROPERTY_SOURCE.FILE;

  const { setPropertyLocation } = useAppContext();
   const { setHideGeoJSON } = useSiteSettingsContext();

  const [step, setStep] = useState(isFile ? 3 : 0);
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(isFile ? propertyData.fileId : null);
  const [detectedUnit, setDetectedUnit] = useState(null);
  const [units, setUnits] = useState(
    isFile ? propertyData.units : UNITS_DEFAULT
  );
  const [center, setCenter] = useState(propertyData.center ?? null);
  /* const [pivot, setPivot] = useState(propertyData.pivot ?? null); */
  const [geoJSON, setGeoJSON] = useState(
    isFile ? propertyData.uploadedPropertyGeoJSON : null
  );

  /* Step 0 - upload file actions */

  const saveFileHandler = (data, file) => {
    setFileId(data.fileId);
    setFile(file);
    setDetectedUnit(data.units);
    setUnits(data.units > 0 ? data.units : UNITS_DEFAULT);
    setStep(1);

    updatePropertyData("uploadedFileName", file.name);
    updatePropertyData(
      "uploadedUnits",
      data.units > 0 ? data.units : UNITS_DEFAULT
    );
    /* updatePropertyData(
      "bearing",
      0
    );
    updatePropertyData(
      "angle",
      0
    ); */

    // wipe downstream state
    setCenter(null);
    /* setPivot(null); */
    setGeoJSON(null);
  };

  const deleteFileHandler = () => {
    setFile(null);
    setFileId(null);

    updatePropertyData("uploadedFileName", null);
    updatePropertyData("uploadedUnits", null);

    // wipe downstream state
    setDetectedUnit(null);
    setUnits(UNITS_DEFAULT);
    setCenter(null);
    /*  setPivot(null); */
    setGeoJSON(null);
  };

  /* Step 1 - set units actions */

  const changeUnitsHandler = (u) => {
    setUnits(u);

    updatePropertyData("uploadedUnits", u);

    // wipe downstream state
    setCenter(null);
    /* setPivot(null); */
    setGeoJSON(null);
  };

  /* Step 2 - set center actions */

  const saveGeoJSONHandler = (convertedGeoJSON, center) => {
    const polygon = convertedGeoJSON.features[0];

    let area = null;
    let perimeter = null;

    if (polygon) {
      area = turf.area(polygon);
      const coordinates = polygon.geometry.coordinates[0];

      perimeter = 0;
      for (let i = 0; i < coordinates.length - 1; i++) {
        perimeter += turf.distance(
          turf.point(coordinates[i]),
          turf.point(coordinates[i + 1]),
          { units: "meters" }
        );
      }
    }

    updatePropertyData(
      "uploadedPropertyPerimeter",
      Math.round(perimeter * 100) / 100
    );
    updatePropertyData("uploadedPropertyArea", Math.round(area * 100) / 100);
    updatePropertyData("uploadedPropertyGeoJSON", convertedGeoJSON);

    setGeoJSON(convertedGeoJSON);
    setCenter(center);
    /* setPivot([center.long, center.lat]); */
    setStep(3);
    setHideGeoJSON(false);
  };

  const reselectCenterHandler = () => {
    /* setPivot(null); */
    setCenter(null);
    setGeoJSON(null);

    // wipe downstream state
    /*  setBearing(0); */
  };

  /*const hideGeoJSON = () => {
    setHideProperty(true);
     updatePropertyData("uploadedPropertyGeoJSON", null); 
  }*/
  /*const displayGeoJSON = (data) => setHideProperty(false);  updatePropertyData("uploadedPropertyGeoJSON", data); */

  const handleBack = () => {
    if (step === 3 && geoJSON) {
      setHideGeoJSON(true);
    }
    setStep((s) => (s > 0 ? s - 1 : s));
  };
  const handleNext = () => {
    if (step === 2 && geoJSON) {
      setHideGeoJSON(false);
    }
    setStep((s) => (s < 3 ? s + 1 : s));
  }

  const saveUploadedFileHandler = async () => {
    const property = {
      ...propertyData.uploadedPropertyGeoJSON,
      features: propertyData.uploadedPropertyGeoJSON.features.filter(
        (f) => f.id === UPLOADED_GEOJSON_FEATURE_ID
      ),
    };
    // Get center
    const centerPolygon = turf.center(property);
    const mainCoordinates = centerPolygon
      ? {
          lat: centerPolygon.geometry.coordinates[1],
          long: centerPolygon.geometry.coordinates[0],
        }
      : null;

    const address = await reverseGeocode(centerPolygon.geometry.coordinates);

    onClose();
    updatePropertyData("propertySource", PROPERTY_SOURCE.FILE);
    updatePropertyData("fileId", fileId);
    updatePropertyData("fileName", propertyData.uploadedFileName);
    updatePropertyData("units", propertyData.uploadedUnits);
    /* updatePropertyData("pivot", pivot); */
    updatePropertyData("center", center);
    updatePropertyData("propertyGeoJSON", property);
    updatePropertyData("perimeter", propertyData.uploadedPropertyPerimeter);
    updatePropertyData("propertyArea", propertyData.uploadedPropertyArea);

    updatePropertyData("coordinatesCenter", mainCoordinates);
    updatePropertyData("location", address ? address : null);
    setPropertyLocation(
      centerPolygon ? centerPolygon.geometry.coordinates : null
    );

    luckyorangeTrackEven("Site Settings: save upload file");
  };

  const reset = () => {
    setFile(null);
    setFileId(propertyData.fileId ?? null);
    setDetectedUnit(null);
    setUnits(propertyData.units ?? UNITS_DEFAULT);
    setCenter(propertyData.center ?? null);
    /*  setPivot(propertyData.pivot ?? null); */
    setGeoJSON(propertyData.propertyGeoJSON ?? null);
    setHideGeoJSON(false);
  };

  const cancelHandler = () => {
    if (!loading) {
      reset();
      setStep(propertyData.fileId ? 3 : 0);
      setHideGeoJSON(false);

      updatePropertyData(
        "uploadedFileName",
        isFile ? propertyData.fileName : null
      );
      updatePropertyData(
        "uploadedUnits",
        isFile ? propertyData.units : UNITS_DEFAULT
      );
      updatePropertyData(
        "uploadedPropertyPerimeter",
        isFile ? propertyData.perimeter : null
      );
      updatePropertyData(
        "uploadedPropertyArea",
        isFile ? propertyData.propertyArea : null
      );
      updatePropertyData(
        "uploadedPropertyGeoJSON",
        isFile ? propertyData.propertyGeoJSON : null
      );
      onClose();
    }
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick") return; // ignore outside clicks
    if (reason === "escapeKeyDown") return; // optional: esc also ignored
    onClose();
  };

  useEffect(() => {
    if (deleteDrawing && !loading) {
      setFile(null);
      setFileId(null);
      setDetectedUnit(null);
      setUnits(UNITS_DEFAULT);
      setCenter(null);
      /* setPivot(null); */
      setGeoJSON(null);
      setStep(0);
      updatePropertyData("propertySource", PROPERTY_SOURCE.HAND_DRAWN);
      updatePropertyData("uploadedPropertyPerimeter", null);
      updatePropertyData("uploadedFileName", null);
      updatePropertyData("uploadedUnits", null);
      updatePropertyData("uploadedPropertyArea", null);
      updatePropertyData("uploadedPropertyGeoJSON", null);
      onClose();
    }
  }, [deleteDrawing]);

  useEffect(() => {
    updatePropertyData("uploadedPropertyPerimeter", null);
    updatePropertyData("uploadedFileName", null);
    updatePropertyData("uploadedUnits", null);
    updatePropertyData("uploadedPropertyArea", null);
    updatePropertyData("uploadedPropertyGeoJSON", null);
    onClose();
  }, []);

  const hasChanges =
    propertyData.uploadedFileName !== propertyData.fileName ||
    propertyData.units !== propertyData.uploadedUnits ||
    (center?.lat !== propertyData.center?.lat &&
      center?.long !== propertyData.center?.long);

  const actionButtons = (
    <>
      <Button
        onClick={cancelHandler}
        disabled={loading}
        sx={{
          background: "transparent",
          color: "#343745",
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          ":hover": {
            boxShadow: 4,
            /*  background: '#fff' */
          },
        }}
      >
        Cancel
      </Button>
      {step > 0 && (
        <Button
          onClick={handleBack}
          disabled={loading}
          sx={{
            background: "#fff",
            color: "#343745",
            textTransform: "none",
            boxShadow: 2,
            ":hover": {
              boxShadow: 4,
            },
          }}
        >
          Back
        </Button>
      )}
      {step < 3 && (
        <Button
          onClick={handleNext}
          disabled={
            loading || (step === 0 && !fileId) || (step === 2 && !center)
          }
          sx={{
            background: "#343745",
            color: "#fff",
            textTransform: "none",
            boxShadow: 2,
            ":disabled": {
              color: "#C7C7D6",
              background: "#EEEEF6",
            },
            ":hover": {
              boxShadow: 4,
            },
          }}
        >
          Next
        </Button>
      )}
      {step === 3 && (
        <Button
          variant="contained"
          onClick={saveUploadedFileHandler}
          disabled={!geoJSON || loading || !hasChanges}
          sx={{
            background: "#EFA21E",
            color: "#fff",
            textTransform: "none",
            boxShadow: 2,
            ":disabled": {
              color: "#C7C7D6",
              background: "#EEEEF6",
            },
            ":hover": {
              boxShadow: 4,
            },
          }}
        >
          Save File
        </Button>
      )}
    </>
  );

  return open ? (
    <>
      {step < 2 ? (
        <Dialog
          open={open}
          /* onClose={handleDialogClose} */
          hideBackdrop={step === 2}
          disableEnforceFocus={step === 2}
          disableScrollLock={step === 2}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": { borderRadius: "16px" },
            "& .MuiDialog-container": {
              pointerEvents: step === 2 ? "none" : "auto",
              alignItems: step === 2 ? "flex-start" : "center",
            },
            "& .MuiPaper-root": {
              pointerEvents: "auto", // keep dialog clickable
              mt: step === 2 ? 2 : 0,
              width: step === 2 ? 600 : undefined,
            },
          }}
        >
          <DialogTitle align="center" mb="16px">
            Upload CAD file
          </DialogTitle>
          <DialogContent>
            <Box mb="24px">
              <StepperCustom steps={UPLOAD_STEPS} activeStep={step} />
            </Box>
            {step === 0 ? (
              <StepUpload
                saveFileHandler={saveFileHandler}
                deleteFileHandler={deleteFileHandler}
                units={units}
                file={file}
                fileId={fileId}
                uploading={loading}
                setUploading={setLoading}
                fileName={propertyData?.uploadedFileName}
              />
            ) : (
              <StepUnits
                detectedUnit={detectedUnit}
                changeUnitsHandler={changeUnitsHandler}
                units={units}
                fileId={fileId}
                propertyFileId={propertyData.fileId}
              />
            )}
          </DialogContent>
          <DialogActions
            sx={{ p: "16px 24px 16px" /* background: "#F3F5F7" */ }}
          >
            {actionButtons}
          </DialogActions>
        </Dialog>
      ) : (
        <Portal>
          <Paper
            elevation={4}
            sx={{
              position: "fixed",
              top: 16,
              right: 16,
              width: "320px",
              height: "calc(100% - 80px - 74px)",
              background: "#FBFBFD",
              borderRadius: "16px",
              position: "absolute",
              top: "80px",
              right: "16px",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              /* alignItems: 'center', */
              justifyContent: "space-between",
              overflow: "auto",
              boxShadow: 3,
              p: "16px",
            }}
          >
            <Box>
              <Typography align="center" mb="16px" fontWeight={500}>
                Upload CAD file
              </Typography>
              <StepperCustom
                steps={UPLOAD_STEPS}
                activeStep={step}
                orientation="vertical"
                withContent={true}
              >
                {step === 2 ? (
                  <StepCenter
                    center={center}
                    reselectCenterHandler={reselectCenterHandler}
                    fileId={fileId}
                    saveGeoJSONHandler={saveGeoJSONHandler}
                    setCenter={setCenter}
                    units={units}
                    loading={loading}
                    setLoading={setLoading}
                  />
                ) : (
                  <StepRotate
                    /* geoJSON={geoJSON} */
                    /* pivot={pivot} */
                    /* updatePropertyData={updatePropertyData}
                    uploadedPropertyGeoJSON={propertyData.uploadedPropertyGeoJSON} */
                    /* bearing={propertyData.bearing }
                    angle={propertyData.angle } */
                  />
                )}{" "}
              </StepperCustom>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              {actionButtons}
            </Box>
          </Paper>
        </Portal>
      )}
    </>
  ) : null;
};
